import { PdfTemplateMapping } from "../../interfaces/dynamicForms/PdfMapping";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetches a specific mapping configuration JSON string.
 */
export async function getMappingConfiguration(
    mappingKey: string,
    authToken: string,
    version: string = 'latest'
): Promise<string | null> { // Returns raw JSON string or null
    const configType = "MAPPING";
    // Encode identifier and version for URL safety
    const encodedIdentifier = encodeURIComponent(mappingKey);
    const encodedVersion = encodeURIComponent(version);

    const url = `${API_BASE_URL}/form/${configType}/${encodedIdentifier}/${encodedVersion}`;
    console.log(`[MappingService] Fetching mapping from: ${url}`);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Accept': 'application/json', // Important: Specify we want JSON
            },
        });

        if (response.status === 404) {
            console.log(`[MappingService] Mapping not found: ${mappingKey}`);
            return null; // Not found is not necessarily an error for the caller
        }
        if (!response.ok) {
            // Try to get error details
            let errorDetails = response.statusText;
            try {
                 const errorBody = await response.json();
                 errorDetails = errorBody?.message || JSON.stringify(errorBody) || errorDetails;
            } catch { /* Ignore if body isn't JSON */ }
            throw new Error(`Failed to fetch mapping: ${response.status} - ${errorDetails}`);
        }

        // IMPORTANT: Assuming the API returns the JSON content directly as the response body
        const jsonContent = await response.text(); // Get raw text
        // We could try parsing here to ensure it's valid JSON before returning?
        try {
            JSON.parse(jsonContent); // Validate JSON structure
            console.log(`[MappingService] Successfully fetched mapping content for ${mappingKey}`);
            return jsonContent;
        } catch (parseError) {
             console.error(`[MappingService] Fetched content for ${mappingKey} is not valid JSON:`, parseError);
             throw new Error(`Received invalid JSON content for mapping '${mappingKey}'.`);
        }

    } catch (error) {
        console.error('[MappingService] Error in getMappingConfiguration:', error);
        throw error; // Re-throw for the component to handle
    }
}

/**
 * Saves (creates or updates) a specific mapping configuration.
 */
export async function saveMappingConfiguration(
    mappingKey: string,
    mappingData: PdfTemplateMapping, // Pass the mapping object
    authToken: string,
    version?: string, // Optional version (defaults to latest on backend)
    description?: string // Optional description
): Promise<void> { // Returns void on success
    const configType = "MAPPING";
    const url = `${API_BASE_URL}/form/${configType}`; // POST to the type endpoint

    const payload = {
        Identifier: mappingKey,
        JsonContent: JSON.stringify(mappingData, null, 2), // Stringify the mapping data
        Version: version, // Pass along if provided
        Description: description
    };
    console.log(`[MappingService] Saving mapping configuration for: ${mappingKey}`);
    // console.log("[MappingService] Payload:", payload.JsonContent); // Careful logging JSON

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            let errorDetails = response.statusText;
            try {
                 const errorBody = await response.json();
                 errorDetails = errorBody?.message || JSON.stringify(errorBody) || errorDetails;
            } catch { /* Ignore if body isn't JSON */ }
             throw new Error(`Failed to save mapping: ${response.status} - ${errorDetails}`);
        }

        console.log(`[MappingService] Successfully saved mapping configuration for ${mappingKey}`);
        // Handle success response body? API returns { message: "..." }
        // const result = await response.json();
        // console.log("[MappingService] Save response:", result);

    } catch (error) {
         console.error('[MappingService] Error in saveMappingConfiguration:', error);
         throw error; // Re-throw for the component to handle
    }
}